import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, MapPin, Phone, Mail, User,
  Briefcase, Clock, Save, Loader2, Pencil,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { formatDate } from '../lib/utils.js';
import type { OnsiteClient, OnsiteJob } from '../../../api/src/types/onsite-types.js';

const STATUS_DOT: Record<string, string> = {
  new: 'bg-purple-500', scheduled: 'bg-blue-500', dispatched: 'bg-cyan-500',
  en_route: 'bg-amber-500', on_site: 'bg-green-500', completed: 'bg-muted-foreground',
  invoiced: 'bg-emerald-500', closed: 'bg-secondary', cancelled: 'bg-red-500',
};

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => onsiteApiFetch<{ client: OnsiteClient; jobs: OnsiteJob[] }>(`/api/v1/onsite/clients/${id}`),
    enabled: !!id,
  });

  const updateClient = useMutation({
    mutationFn: (patch: Partial<OnsiteClient>) =>
      onsiteApiFetch<{ client: OnsiteClient }>(`/api/v1/onsite/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', id] });
      setEditing(false);
    },
  });

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const { client, jobs } = data;

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate('/clients')} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">{client.name}</h1>
            <p className="text-sm text-muted-foreground">Client since {formatDate(client.created_at)}</p>
          </div>
          <button
            onClick={() => setEditing(e => !e)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        </div>

        {/* Client info */}
        <div className="mb-6 rounded-xl border border-border bg-card p-4 space-y-3">
          {editing ? (
            <EditForm client={client} onSave={updateClient.mutateAsync} isPending={updateClient.isPending} onCancel={() => setEditing(false)} />
          ) : (
            <>
              {client.address && (
                <InfoRow icon={MapPin} label="Address" value={`${client.address}${client.city ? `, ${client.city}` : ''}${client.state ? `, ${client.state}` : ''} ${client.zip ?? ''}`} />
              )}
              {client.phone && <InfoRow icon={Phone} label="Phone" value={client.phone} />}
              {client.email && <InfoRow icon={Mail} label="Email" value={client.email} />}
              {client.primary_contact_name && (
                <InfoRow icon={User} label="Primary Contact" value={`${client.primary_contact_name}${client.primary_contact_phone ? ` (${client.primary_contact_phone})` : ''}`} />
              )}
              {client.notes && (
                <div className="rounded-lg bg-secondary p-3 text-sm text-foreground">{client.notes}</div>
              )}
            </>
          )}
        </div>

        {/* Job history */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Job History ({jobs.length})</h2>
            <Link
              to={`/jobs/new?client_id=${client.id}`}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary"
            >
              <Briefcase className="h-3 w-3" /> New Job
            </Link>
          </div>
          {jobs.length === 0 ? (
            <div className="rounded-xl border border-border bg-card py-8 text-center text-sm text-muted-foreground">
              No jobs for this client yet
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map(job => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 hover:bg-secondary/50"
                >
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[job.status] ?? 'bg-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground line-clamp-1">{job.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(job.scheduled_start)} -- {job.status.replace('_', ' ')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
}

function EditForm({ client, onSave, isPending, onCancel }: {
  client: OnsiteClient;
  onSave: (patch: Partial<OnsiteClient>) => Promise<unknown>;
  isPending: boolean;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: client.name,
    address: client.address,
    city: client.city ?? '',
    state: client.state ?? '',
    zip: client.zip ?? '',
    phone: client.phone ?? '',
    email: client.email ?? '',
    primary_contact_name: client.primary_contact_name ?? '',
    primary_contact_phone: client.primary_contact_phone ?? '',
    notes: client.notes ?? '',
  });

  return (
    <form onSubmit={async (e) => { e.preventDefault(); await onSave(form); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Name *</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="input" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Email</label>
          <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="input" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Address</label>
        <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="input" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City" className="input" />
        <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="State" className="input" />
        <input value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} placeholder="ZIP" className="input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="input" />
        <input value={form.primary_contact_name} onChange={e => setForm(f => ({ ...f, primary_contact_name: e.target.value }))} placeholder="Primary Contact" className="input" />
      </div>
      <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes..." rows={2} className="input resize-none" />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={isPending} className="btn-primary">
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>
    </form>
  );
}
